import Layout from "../components/Layout.tsx";
import { useEffect, useState, useRef } from "react";
import { useRoute, Link } from "wouter";
import toast from "react-hot-toast";
import { Button } from "../components/Button.tsx";
import { Badge } from "../components/Badge.tsx";
import { Dialog, DialogTitle } from "../components/Dialog.tsx";
import { ProgressStepper, StatusLine, LoadingRow, PulsingDot, useElapsedTimer } from "../components/RunProgress.tsx";
import { cancelRun, fetchDryRunData, fetchRunDetails, retryFailedItems } from "../services/ApiService.ts";
import {
  DryRunModuleData,
  RunDetails,
  LoonModuleResult,
  InkoopVerkoopModuleResult,
  StamgegevensChangedField,
  StamgegevensModuleResult,
} from "../types.ts";
import { MODULE_LABELS, STAMGEGEVENS_CHANGED_FIELD_LABELS, getModuleResultBadge, getResultBadge, getStatusColor, getStatusLabel, getTotalSyncedCount, parseUtcDate } from "../utils.ts";
import { isReadOnly } from "../config.ts";

/* ───────────── constants ───────────── */

const SYNC_STEPS = ["Opstarten", "Synchroniseren", "Voltooid"];

type Phase = "scheduled" | "syncing" | "completed" | "partial_success" | "failed" | "cancelled";

const PHASE_INFO: Record<Phase, { label: string; description: string }> = {
  scheduled:       { label: "Opstarten",       description: "Het proces wordt opgestart..." },
  syncing:         { label: "Synchroniseren",  description: "Data wordt gesynchroniseerd naar Exact Online..." },
  completed:       { label: "Voltooid",        description: "Synchronisatie is afgerond" },
  partial_success: { label: "Deels geslaagd",  description: "Synchronisatie is afgerond, maar niet alles is gelukt" },
  failed:          { label: "Mislukt",         description: "Er is een fout opgetreden" },
  cancelled:       { label: "Geannuleerd",     description: "De synchronisatie is geannuleerd" },
};

const PHASE_TO_STEP: Record<Phase, number> = {
  scheduled: 0,
  syncing: 1,
  completed: 2,
  partial_success: 2,
  failed: -1,
  cancelled: -1,
};

const moduleCursorFormatter = new Intl.DateTimeFormat("nl-NL", {
  day: "2-digit",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

function formatChangedFieldValue(value: string | null): string {
  if (value == null || value === "") {
    return "(leeg)";
  }
  return value;
}

function formatModuleQueryFrom(value: string | null): string {
  if (!value) {
    return "geen startgrens";
  }

  const parsed = parseUtcDate(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return moduleCursorFormatter.format(parsed);
}

function renderChangedFieldLabel(change: StamgegevensChangedField): string {
  return STAMGEGEVENS_CHANGED_FIELD_LABELS[change.field] ?? change.field;
}

/* ───────────── sub-components ───────────── */

function ModuleProgress({
  runId,
  moduleKey,
  label,
  result,
  isCompleted,
  dryRunModule,
  onRetried,
  debiteurenSyncDisabled = false,
}: {
  runId: number;
  moduleKey: "verkoop" | "inkoop" | "loon";
  label: string;
  result: LoonModuleResult | InkoopVerkoopModuleResult | null;
  isCompleted: boolean;
  dryRunModule?: DryRunModuleData | null;
  onRetried: () => void;
  debiteurenSyncDisabled?: boolean;
}) {
  const [showItems, setShowItems] = useState(false);
  const [retryModalOpen, setRetryModalOpen] = useState(false);
  const [retrying, setRetrying] = useState(false);

  if (!result) {
    return (
      <div className="rounded-xl border border-gray-100 bg-gray-50/50 p-4">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-500">{label}</span>
          {isCompleted ? (
            <Badge color="zinc">Niet actief</Badge>
          ) : (
            <span className="text-xs text-gray-400">Wachten...</span>
          )}
        </div>
      </div>
    );
  }

  const items = "processedRegelnummers" in result
    ? result.processedRegelnummers
    : result.processedPostnummers;

  const succeeded = items.filter((i) => i.status === "SUCCESS").length;
  const warnings = items.filter((i) => i.status === "SUCCESS" && !!i.error).length;
  const failed = items.filter((i) => i.status === "FAILED").length;
  const cancelled = items.filter((i) => i.status === "CANCELLED").length;
  const total = items.length;
  const isRunning = result.status === null;
  const showDebiteurenHint = debiteurenSyncDisabled && items.some(
    (i) => i.status === "FAILED" && !!i.error?.startsWith("Geen Exact relatie gevonden"),
  );
  const journaalpostCount = dryRunModule?.journaalpostCount ?? null;
  const hasDryRunGrouping = (
    dryRunModule &&
    journaalpostCount != null &&
    journaalpostCount !== dryRunModule.recordCount
  );

  const handleConfirmRetry = async () => {
    setRetrying(true);
    try {
      const { error } = await retryFailedItems(runId, moduleKey);
      if (error) {
        toast.error(error);
      } else {
        toast.success("Mislukte items worden opnieuw geprobeerd.");
        onRetried();
      }
    } catch {
      toast.error("Er is een fout opgetreden bij het opnieuw proberen.");
    } finally {
      setRetrying(false);
      setRetryModalOpen(false);
    }
  };

  return (
    <>
    <div
      className={`rounded-xl border p-4 transition-all duration-200 ${
        isRunning
          ? "border-blue-200 bg-blue-50/30"
          : result.status === "FAILED"
            ? "border-red-200 bg-red-50/30"
            : result.status === "PARTIAL_SUCCESS"
              ? "border-yellow-200 bg-yellow-50/30"
              : result.status === "CANCELLED"
                ? "border-gray-200 bg-gray-50/50"
                : "border-green-200 bg-green-50/30"
      }`}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {isRunning && <PulsingDot />}
          <span className="text-sm font-medium text-gray-900">{label}</span>
        </div>
        <div className="flex items-center gap-2">
          {!isRunning && failed > 0 && !result.retryRunId && (
            <button
              onClick={() => setRetryModalOpen(true)}
              disabled={isReadOnly}
              className="text-xs text-blue-600 hover:text-blue-800 transition-colors disabled:pointer-events-none disabled:opacity-50"
            >
              Mislukte items opnieuw proberen
            </button>
          )}
          {(() => {
            const badge = getModuleResultBadge(result.status, total);
            return <Badge color={badge.color} title={badge.title}>{badge.label}</Badge>;
          })()}
        </div>
      </div>

      {result.retryRunId && (
        <div className="mb-3 flex items-center gap-2 rounded-lg border border-blue-100 bg-blue-50/60 px-3 py-2 text-xs text-blue-800">
          <span>Opnieuw geprobeerd &rarr;</span>
          <Link href={`/synchronisatie/${result.retryRunId}`} className="font-medium underline hover:no-underline">
            Run #{result.retryRunId}
          </Link>
          <Badge color={getStatusColor(result.retryRunResult)}>
            {result.retryRunState === "COMPLETED" ? getStatusLabel(result.retryRunResult) : "Bezig"}
          </Badge>
        </div>
      )}

      <div className="mb-3 inline-flex min-h-8 items-center gap-2 rounded-full border border-white/80 bg-white/80 px-3 py-1 text-xs text-gray-600 shadow-sm shadow-gray-200/40">
        <span className="font-medium text-gray-700">Wijzigingen sinds:</span>
        <span className="tabular-nums text-gray-900">{formatModuleQueryFrom(result.queryFromChangedAt)}</span>
      </div>

      {/* Counts */}
      <div className="mb-3 flex flex-wrap items-center gap-4">
        <div className="flex items-baseline gap-1">
          <span className="text-2xl font-semibold tabular-nums text-gray-900">{total}</span>
          <span className="text-xs text-gray-500">{total === 1 ? "journaalpost verwerkt" : "journaalposten verwerkt"}</span>
        </div>
        {succeeded > 0 && (
          <div className="flex items-baseline gap-1">
            <span className="text-sm font-medium tabular-nums text-green-700">{succeeded}</span>
            <span className="text-xs text-green-600">geslaagd</span>
          </div>
        )}
        {warnings > 0 && (
          <div className="flex items-baseline gap-1">
            <span className="text-sm font-medium tabular-nums text-amber-700">{warnings}</span>
            <span className="text-xs text-amber-600">waarschuwing{warnings === 1 ? "" : "en"}</span>
          </div>
        )}
        {failed > 0 && (
          <div className="flex items-baseline gap-1">
            <span className="text-sm font-medium tabular-nums text-red-700">{failed}</span>
            <span className="text-xs text-red-600">gefaald</span>
          </div>
        )}
        {cancelled > 0 && (
          <div className="flex items-baseline gap-1">
            <span className="text-sm font-medium tabular-nums text-gray-600">{cancelled}</span>
            <span className="text-xs text-gray-500">geannuleerd</span>
          </div>
        )}
      </div>
      {hasDryRunGrouping && (
        <div className="mb-3 text-xs text-gray-400">
          {dryRunModule.recordCount} onderliggende journaalpostregels
        </div>
      )}

      {/* Progress bar */}
      {total > 0 && (
        <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden mb-2">
          <div className="h-full flex">
            {succeeded > 0 && (
              <div
                className="bg-green-500 transition-all duration-500"
                style={{ width: `${(succeeded / total) * 100}%` }}
              />
            )}
            {failed > 0 && (
              <div
                className="bg-red-500 transition-all duration-500"
                style={{ width: `${(failed / total) * 100}%` }}
              />
            )}
            {cancelled > 0 && (
              <div
                className="bg-gray-400 transition-all duration-500"
                style={{ width: `${(cancelled / total) * 100}%` }}
              />
            )}
          </div>
        </div>
      )}

      {/* Error */}
      {result.error && (
        <div className="mt-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {result.error}
        </div>
      )}

      {showDebiteurenHint && (
        <div className="mt-2 px-3 py-2 bg-blue-50 border border-blue-100 rounded-lg text-xs text-blue-800">
          Ontbrekende relaties kunnen automatisch in Exact worden aangemaakt door de debiteuren-synchronisatie aan te zetten in de koppeling.
        </div>
      )}

      {/* Expandable item list */}
      {total > 0 && (
        <div className="mt-2">
          <button
            onClick={() => setShowItems(!showItems)}
            className="text-xs text-blue-600 hover:text-blue-800 transition-colors"
          >
            {showItems ? "Verberg details" : `Bekijk ${total} ${total === 1 ? "journaalpost" : "journaalposten"}`}
          </button>
          {showItems && (
            <div className="mt-2 max-h-48 overflow-auto rounded-lg border border-gray-200 bg-white">
              <div className="divide-y divide-gray-100">
                {items.map((item, idx) => {
                  const isWarning = item.status === "SUCCESS" && !!item.error;
                  return (
                    <div key={idx} className="flex items-center justify-between px-3 py-1.5 text-xs">
                      <span className="font-mono tabular-nums text-gray-700 shrink-0">{item.nummer}</span>
                      <div className="flex items-center gap-2 min-w-0">
                        <Badge color={isWarning ? "amber" : getStatusColor(item.status)}>
                          {isWarning ? "Waarschuwing" : getStatusLabel(item.status)}
                        </Badge>
                        {item.error && (
                          <span className={`break-all ${isWarning ? "text-amber-600" : "text-red-600"}`}>
                            {item.error}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>

    {/* Retry confirmation modal */}
    <Dialog open={retryModalOpen} onClose={() => setRetryModalOpen(false)}>
      <DialogTitle className="text-lg font-semibold text-gray-900">
        Mislukte items opnieuw proberen
      </DialogTitle>
      <p className="mt-3 text-sm text-gray-600">
        Dit start een nieuwe synchronisatie voor de {label} die zijn mislukt. Geslaagde items worden niet opnieuw verwerkt.
      </p>
      <div className="mt-6 flex gap-3 justify-end">
        <button
          onClick={() => setRetryModalOpen(false)}
          className="px-4 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
          disabled={retrying}
        >
          Terug
        </button>
        <Button color="dark" onClick={handleConfirmRetry} disabled={retrying}>
          {retrying ? "Bezig..." : "Ja, opnieuw proberen"}
        </Button>
      </div>
    </Dialog>
    </>
  );
}

function StamgegevensModuleProgress({
  label,
  result,
  isCompleted,
}: {
  label: string;
  result: StamgegevensModuleResult | null;
  isCompleted: boolean;
}) {
  const [showItems, setShowItems] = useState(false);

  if (!result) {
    return (
      <div className="rounded-xl border border-gray-100 bg-gray-50/50 p-4">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-500">{label}</span>
          {isCompleted ? (
            <Badge color="zinc">Niet actief</Badge>
          ) : (
            <span className="text-xs text-gray-400">Wachten...</span>
          )}
        </div>
      </div>
    );
  }

  const total = result.createdCount + result.updatedCount + result.failedCount;
  const isRunning = result.status === null;

  return (
    <div
      className={`rounded-xl border p-4 transition-all duration-200 ${
        isRunning
          ? "border-blue-200 bg-blue-50/30"
          : result.status === "FAILED"
            ? "border-red-200 bg-red-50/30"
            : result.status === "PARTIAL_SUCCESS"
              ? "border-yellow-200 bg-yellow-50/30"
              : result.status === "CANCELLED"
                ? "border-gray-200 bg-gray-50/50"
                : "border-green-200 bg-green-50/30"
      }`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {isRunning && <PulsingDot />}
          <span className="text-sm font-medium text-gray-900">{label}</span>
        </div>
        {(() => {
          const badge = getModuleResultBadge(result.status, total);
          return <Badge color={badge.color} title={badge.title}>{badge.label}</Badge>;
        })()}
      </div>

      <div className="flex items-center gap-4 mb-3">
        {result.createdCount > 0 && (
          <div className="flex items-baseline gap-1">
            <span className="text-sm font-medium tabular-nums text-blue-700">{result.createdCount}</span>
            <span className="text-xs text-blue-600">aangemaakt</span>
          </div>
        )}
        {result.updatedCount > 0 && (
          <div className="flex items-baseline gap-1">
            <span className="text-sm font-medium tabular-nums text-green-700">{result.updatedCount}</span>
            <span className="text-xs text-green-600">bijgewerkt</span>
          </div>
        )}
        {result.failedCount > 0 && (
          <div className="flex items-baseline gap-1">
            <span className="text-sm font-medium tabular-nums text-red-700">{result.failedCount}</span>
            <span className="text-xs text-red-600">gefaald</span>
          </div>
        )}
      </div>

      {!isRunning && total === 0 && !result.error && result.status !== "CANCELLED" && (
        <div className="text-xs text-gray-500">Geen wijzigingen</div>
      )}

      {result.error && (
        <div className="mt-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {result.error}
        </div>
      )}

      {total > 0 && (
        <div className="mt-2">
          <button
            onClick={() => setShowItems(!showItems)}
            className="text-xs text-blue-600 hover:text-blue-800 transition-colors"
          >
            {showItems ? "Verberg details" : `Bekijk ${total} items`}
          </button>
          {showItems && (
            <div className="mt-2 max-h-64 overflow-auto rounded-lg border border-gray-200 bg-white">
              <div className="divide-y divide-gray-100">
                {result.items.map((item, idx) => (
                  <div key={idx} className="px-3 py-2 text-xs">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <span className="font-mono tabular-nums text-gray-700">{item.code}</span>
                        {item.name && <span className="text-gray-500">{item.name}</span>}
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge color={item.action === "CREATED" ? "blue" : "green"}>
                          {item.action === "CREATED" ? "Aangemaakt" : "Bijgewerkt"}
                        </Badge>
                        {item.status === "FAILED" && (
                          <Badge color={getStatusColor(item.status)}>{getStatusLabel(item.status)}</Badge>
                        )}
                        {item.error && (
                          <span className="text-red-600 break-all">
                            {item.error}
                          </span>
                        )}
                      </div>
                    </div>

                    {item.changedFields && item.changedFields.length > 0 && (
                      <div className="mt-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                        <div className="text-[11px] font-medium uppercase tracking-wide text-gray-500">
                          Gewijzigde velden
                        </div>
                        <div className="mt-2 space-y-1">
                          {item.changedFields.map((change) => (
                            <div
                              key={`${item.code}-${change.field}`}
                              className="grid grid-cols-[110px_minmax(0,1fr)_20px_minmax(0,1fr)] items-start gap-2"
                            >
                              <span className="text-gray-500">{renderChangedFieldLabel(change)}</span>
                              <span className="truncate text-gray-600" title={formatChangedFieldValue(change.old)}>
                                {formatChangedFieldValue(change.old)}
                              </span>
                              <span className="text-center text-gray-400">-&gt;</span>
                              <span className="truncate font-medium text-gray-900" title={formatChangedFieldValue(change.new)}>
                                {formatChangedFieldValue(change.new)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ───────────── main component ───────────── */

const SyncRunProgress = () => {
  const [, params] = useRoute("/synchronisatie/:runId");
  const runId = params?.runId ? parseInt(params.runId) : null;

  const [run, setRun] = useState<RunDetails | null>(null);
  const [sourceDryRunModules, setSourceDryRunModules] = useState<Record<string, DryRunModuleData>>({});
  const [phase, setPhase] = useState<Phase | null>(null);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [cancelRequestedLocal, setCancelRequestedLocal] = useState(false);

  const isActive = phase === "scheduled" || phase === "syncing";
  const cancelRequested = cancelRequestedLocal || Boolean(run?.cancelRequestedAt);

  const handleConfirmCancel = async () => {
    if (!runId) return;
    setCancelling(true);
    setCancelError(null);
    const { error } = await cancelRun(runId);
    setCancelling(false);
    if (error) {
      setCancelError(error);
      return;
    }
    setCancelRequestedLocal(true);
    setCancelModalOpen(false);
  };
  const elapsed = useElapsedTimer(isActive, run?.runDatetime, !isActive ? run?.updatedAt : null);

  // Re-fetch on demand (e.g. after starting a retry) - the polling loop below has already
  // stopped by the time a completed run's module gets a retry, so it won't pick this up on its own.
  const refreshRun = async () => {
    if (!runId) return;
    try {
      const details = await fetchRunDetails(runId);
      setRun(details);
    } catch {
      // Ignore - the user can still reload manually.
    }
  };

  // Poll run details
  const pollTimeout = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (!runId) return;
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      try {
        const details = await fetchRunDetails(runId);
        if (cancelled) return;
        setRun(details);

        if (details.state === "SCHEDULED") {
          setPhase("scheduled");
        } else if (details.state === "RUNNING") {
          setPhase("syncing");
        } else if (details.state === "COMPLETED") {
          setPhase(
            details.result === "FAILED"
              ? "failed"
              : details.result === "CANCELLED"
                ? "cancelled"
                : details.result === "PARTIAL_SUCCESS"
                  ? "partial_success"
                  : "completed",
          );
          return; // Stop polling
        }
      } catch {
        // Keep polling on error
      }
      if (!cancelled) pollTimeout.current = setTimeout(poll, 3000);
    };

    poll();
    return () => { cancelled = true; clearTimeout(pollTimeout.current); };
  }, [runId]);

  useEffect(() => {
    if (!run?.sourceRunId) {
      setSourceDryRunModules({});
      return;
    }

    let cancelled = false;

    const loadSourceDryRun = async () => {
      try {
        const preview = await fetchDryRunData(run.sourceRunId);
        if (cancelled) return;

        setSourceDryRunModules(
          Object.fromEntries(preview.modules.map((mod) => [mod.module, mod])),
        );
      } catch {
        if (!cancelled) {
          setSourceDryRunModules({});
        }
      }
    };

    loadSourceDryRun();
    return () => { cancelled = true; };
  }, [run?.sourceRunId]);

  const totalProcessed = run ? getTotalSyncedCount(run) : 0;

  const stepIdx = phase ? PHASE_TO_STEP[phase] : -1;
  const stepTitle = run
    ? `${run.dryRun ? "Proefdraai" : "Synchronisatie"}: ${run.connectionName}`
    : "Synchronisatie";

  if (!phase || !run) {
    return (
      <Layout
        title=""
        description=""
        stepTitle="Synchronisatie"
        showBackButton={false}
        showNextButton={false}
      >
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <LoadingRow key={i} />
          ))}
        </div>
      </Layout>
    );
  }

  return (
    <Layout
      title=""
      description=""
      stepTitle={stepTitle}
      showBackButton={false}
      showNextButton={false}
    >
      <div className="space-y-6">
        {/* Progress stepper */}
        <ProgressStepper
          steps={SYNC_STEPS}
          currentStep={stepIdx}
          failedStep={phase === "failed" ? 1 : undefined}
        />

        {/* Status line */}
        <StatusLine
          label={PHASE_INFO[phase].label}
          description={PHASE_INFO[phase].description}
          elapsed={elapsed}
          isActive={isActive}
          extra={totalProcessed > 0 ? (
            <>
              <span className="text-gray-300">|</span>
              <span className="tabular-nums">{totalProcessed} items verwerkt</span>
            </>
          ) : undefined}
        />

        {/* Module cards */}
        {run && phase !== "scheduled" && (
          <>
            <div>
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Stamgegevens</h3>
              <div className="space-y-3">
                <StamgegevensModuleProgress
                  label={MODULE_LABELS.debiteuren}
                  result={run.debiteurenResult}
                  isCompleted={!isActive}
                />
                <StamgegevensModuleProgress
                  label={MODULE_LABELS.grootboeken}
                  result={run.grootboekenResult}
                  isCompleted={!isActive}
                />
                <StamgegevensModuleProgress
                  label={MODULE_LABELS.kostenplaatsen}
                  result={run.kostenplaatsenResult}
                  isCompleted={!isActive}
                />
              </div>
            </div>

            <div>
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Boekingen</h3>
              <div className="space-y-3">
                <ModuleProgress
                  runId={run.id}
                  moduleKey="verkoop"
                  label={MODULE_LABELS.verkoop}
                  result={run.verkoopResult}
                  isCompleted={!isActive}
                  dryRunModule={sourceDryRunModules.verkoop}
                  onRetried={refreshRun}
                  debiteurenSyncDisabled={!run.enableSyncDebiteuren}
                />
                <ModuleProgress
                  runId={run.id}
                  moduleKey="inkoop"
                  label={MODULE_LABELS.inkoop}
                  result={run.inkoopResult}
                  isCompleted={!isActive}
                  dryRunModule={sourceDryRunModules.inkoop}
                  onRetried={refreshRun}
                />
                <ModuleProgress
                  runId={run.id}
                  moduleKey="loon"
                  label={MODULE_LABELS.loon}
                  result={run.loonResult}
                  isCompleted={!isActive}
                  dryRunModule={sourceDryRunModules.loon}
                  onRetried={refreshRun}
                />
              </div>
            </div>
          </>
        )}

        {/* Skeleton during scheduled */}
        {phase === "scheduled" && (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <LoadingRow key={i} />
            ))}
          </div>
        )}

        {/* Run error */}
        {run?.error && (
          <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            <span className="font-medium">Fout: </span>{run.error}
          </div>
        )}

        {/* Overall result badge */}
        {!isActive && run && (
          <div className="flex flex-wrap items-center gap-3 text-sm">
            {(() => {
              const badge = getResultBadge(run);
              return <Badge color={badge.color} title={badge.title}>{badge.label}</Badge>;
            })()}
            <span className="text-gray-400">|</span>
            <span className="text-gray-600">
              <strong className="text-gray-900">{totalProcessed}</strong> items verwerkt
            </span>
          </div>
        )}

        {/* Action buttons */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
          <Button color="dark" href="/synchronisaties">
            {isActive ? "Naar overzicht" : "Terug"}
          </Button>
          {isActive && (
            cancelRequested ? (
              <span className="inline-flex items-center gap-2 text-sm text-gray-500">
                <PulsingDot />
                Annulering aangevraagd...
              </span>
            ) : (
              <Button color="red" onClick={() => setCancelModalOpen(true)} disabled={isReadOnly}>
                Annuleren
              </Button>
            )
          )}
        </div>
      </div>

      {/* Cancel confirmation modal */}
      <Dialog open={cancelModalOpen} onClose={() => setCancelModalOpen(false)}>
        <DialogTitle className="text-lg font-semibold text-gray-900">
          Synchronisatie annuleren
        </DialogTitle>
        <p className="mt-3 text-sm text-gray-600">
          Weet je zeker dat je deze synchronisatie wilt annuleren? Journaalposten die al
          verwerkt zijn blijven staan in Exact Online. Nog niet verwerkte journaalposten
          krijgen de status geannuleerd en worden bij een volgende synchronisatie opnieuw
          opgepakt.
        </p>
        {cancelError && (
          <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {cancelError}
          </div>
        )}
        <div className="mt-6 flex gap-3 justify-end">
          <button
            onClick={() => setCancelModalOpen(false)}
            className="px-4 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
            disabled={cancelling}
          >
            Terug
          </button>
          <Button color="red" onClick={handleConfirmCancel} disabled={cancelling}>
            {cancelling ? "Bezig..." : "Ja, annuleer"}
          </Button>
        </div>
      </Dialog>
    </Layout>
  );
};

export default SyncRunProgress;
